import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import marketplaceService from '../../services/marketplaceService';
import { supabase } from '../../lib/supabase';
import AuctionFloorDetail from './components/AuctionFloorDetail';
import { useNavigate } from 'react-router-dom';

const getTimeRemaining = (endDate) => {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end - now;
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getUrgencyColor = (endDate) => {
  const diff = new Date(endDate) - new Date();
  if (diff <= 0) return 'text-muted-foreground';
  if (diff < 1000 * 60 * 60) return 'text-destructive';
  if (diff < 1000 * 60 * 60 * 6) return 'text-warning';
  return 'text-success';
};

const AuctionFloor = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [filters, setFilters] = useState({ search: '', auctionType: '', collectibleType: '' });
  const [stats, setStats] = useState({ total: 0, active: 0, ending_soon: 0, premiere: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const channelRef = useRef(null);
  const activityChannelRef = useRef(null);

  const loadAuctions = useCallback(async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getActiveAuctions(filters);
    if (!error && data) {
      setAuctions(data);
      const now = new Date();
      setStats({
        total: data?.length,
        active: data?.filter(a => a?.status === 'active')?.length,
        ending_soon: data?.filter(a => {
          const diff = new Date(a?.end_date) - now;
          return diff > 0 && diff < 1000 * 60 * 60 * 6;
        })?.length,
        premiere: data?.filter(a => a?.auction_type === 'premiere')?.length,
      });
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
      return;
    }
    loadAuctions();
  }, []);

  // Real-time subscription: refresh auction list when any bid is placed
  useEffect(() => {
    const channelName = `auction_floor_bids:${Date.now()}`;

    if (!supabase) return;

    const channel = supabase?.channel(channelName);

    channel?.on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'auction_bids'
    }, (payload) => {
        // Update the specific auction's bid count and current bid in state
        setAuctions(prev => prev?.map(a => {
          if (a?.id === payload?.new?.auction_id) {
            return {
              ...a,
              current_bid: payload?.new?.bid_amount,
              bid_count: (a?.bid_count || 0) + 1
            };
          }
          return a;
        }));
        // Add to recent activity
        setRecentActivity(prev => [{
          id: payload?.new?.id,
          auction_id: payload?.new?.auction_id,
          bid_amount: payload?.new?.bid_amount,
          created_at: payload?.new?.created_at || new Date()?.toISOString()
        }, ...prev]?.slice(0, 10));
      });

    channel?.subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => loadAuctions();

  const clearFilters = () => {
    setFilters({ search: '', auctionType: '', collectibleType: '' });
    setTimeout(() => loadAuctions(), 50);
  };

  const filteredAuctions = auctions?.filter(a => {
    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      if (!a?.title?.toLowerCase()?.includes(q) && !a?.description?.toLowerCase()?.includes(q)) return false;
    }
    if (filters?.auctionType && a?.auction_type !== filters?.auctionType) return false;
    if (filters?.collectibleType && a?.collectible?.collectible_type !== filters?.collectibleType) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} min-h-screen`}>
          <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center space-x-3 mb-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Icon name="Gavel" size={20} className="text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground">Auction Floor</h1>
                  <span className="flex items-center space-x-1.5 bg-success/10 text-success text-xs font-semibold px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    <span>Live</span>
                  </span>
                </div>
                <p className="text-sm text-muted-foreground ml-13">Browse live auctions and place real-time bids</p>
              </div>
              <Button onClick={loadAuctions} variant="outline" iconName="RefreshCw" iconPosition="left" size="sm">
                Refresh
              </Button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Auctions', value: stats?.total, icon: 'LayoutGrid', color: 'text-primary' },
                { label: 'Active Now', value: stats?.active, icon: 'Activity', color: 'text-success' },
                { label: 'Ending Soon', value: stats?.ending_soon, icon: 'Timer', color: 'text-warning' },
                { label: 'Premiere', value: stats?.premiere, icon: 'Star', color: 'text-yellow-500' },
              ]?.map((stat) => (
                <div key={stat?.label} className="bg-card border border-border rounded-xl p-4 flex items-center space-x-4">
                  <div className="w-10 h-10 bg-muted/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={stat?.icon} size={18} className={stat?.color} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-card-foreground">{stat?.value}</p>
                    <p className="text-xs text-muted-foreground">{stat?.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  placeholder="Search auctions..."
                  value={filters?.search}
                  onChange={(e) => handleFilterChange('search', e?.target?.value)}
                />
                <Select
                  value={filters?.auctionType}
                  onChange={(e) => handleFilterChange('auctionType', e?.target?.value)}
                >
                  <option value="">All Auction Types</option>
                  <option value="regular">Regular</option>
                  <option value="premiere">Premiere</option>
                </Select>
                <Select
                  value={filters?.collectibleType}
                  onChange={(e) => handleFilterChange('collectibleType', e?.target?.value)}
                >
                  <option value="">All Collectible Types</option>
                  <option value="vintage-watches">Vintage Watches</option>
                  <option value="rare-coins">Rare Coins</option>
                  <option value="sports-cards">Sports Cards</option>
                  <option value="comics">Comics</option>
                  <option value="books">First Editions</option>
                </Select>
              </div>
              <div className="flex space-x-3 mt-4">
                <Button onClick={applyFilters} iconName="Search" iconPosition="left" size="sm">
                  Search
                </Button>
                <Button onClick={clearFilters} variant="outline" iconName="X" iconPosition="left" size="sm">
                  Clear
                </Button>
              </div>
            </div>

            {/* Recent Activity Ticker */}
            {recentActivity?.length > 0 && (
              <div className="bg-card border border-border rounded-xl px-5 py-3 flex items-center space-x-3 overflow-hidden">
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Live</span>
                </div>
                <div className="flex items-center space-x-4 overflow-x-auto scrollbar-hide">
                  {recentActivity?.slice(0, 5)?.map((act, idx) => {
                    const auction = auctions?.find(a => a?.id === act?.auction_id);
                    return (
                      <span key={act?.id || idx} className="text-sm text-card-foreground whitespace-nowrap flex items-center space-x-1.5">
                        <Icon name="Gavel" size={12} className="text-primary flex-shrink-0" />
                        <span>
                          New bid <span className="font-semibold text-primary">${parseFloat(act?.bid_amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          {auction?.title && <span className="text-muted-foreground"> on {auction?.title?.slice(0, 24)}{auction?.title?.length > 24 ? '…' : ''}</span>}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Auction Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6]?.map(i => (
                  <div key={i} className="bg-card border border-border rounded-xl h-96 animate-pulse" />
                ))}
              </div>
            ) : filteredAuctions?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-16 text-center">
                <Icon name="Gavel" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No live auctions found</h3>
                <p className="text-muted-foreground text-sm">Try adjusting your filters or check back soon</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAuctions?.map(auction => {
                  const urgencyColor = getUrgencyColor(auction?.end_date);
                  const timeLeft = getTimeRemaining(auction?.end_date);
                  const isEnded = timeLeft === 'Ended';

                  return (
                    <div
                      key={auction?.id}
                      className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-premium-lg hover:border-primary/30 transition-all duration-300 cursor-pointer group"
                      onClick={() => setSelectedAuction(auction)}
                    >
                      {/* Image */}
                      <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                        {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                          <img
                            src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                            alt={auction?.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Image" size={40} className="text-muted-foreground" />
                          </div>
                        )}

                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-col space-y-1.5">
                          {auction?.auction_type === 'premiere' && (
                            <span className="bg-yellow-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                              <Icon name="Star" size={11} />
                              <span>Premiere</span>
                            </span>
                          )}
                          {auction?.status === 'active' && (
                            <span className="bg-success/90 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                              <span>Live</span>
                            </span>
                          )}
                        </div>

                        {/* Time Remaining */}
                        <div className={`absolute top-3 right-3 bg-black/75 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 ${isEnded ? 'text-muted-foreground' : urgencyColor}`}>
                          <Icon name="Clock" size={11} />
                          <span>{timeLeft}</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 space-y-3">
                        <div>
                          <h3 className="font-semibold text-card-foreground line-clamp-1 group-hover:text-primary transition-colors">
                            {auction?.title}
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{auction?.description}</p>
                        </div>

                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">Current Bid</p>
                            <p className="text-xl font-bold text-primary">
                              ${parseFloat(auction?.current_bid || auction?.starting_bid || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground mb-0.5">Bids</p>
                            <p className="text-sm font-semibold text-card-foreground">{auction?.bid_count || 0}</p>
                          </div>
                        </div>

                        {/* Seller */}
                        {auction?.seller && (
                          <div className="flex items-center space-x-2 pt-1 border-t border-border/50">
                            <div className="w-5 h-5 rounded-full bg-muted overflow-hidden flex-shrink-0">
                              {auction?.seller?.avatar_url ? (
                                <img src={auction?.seller?.avatar_url} alt={auction?.seller?.full_name || 'Seller'} className="w-full h-full object-cover" />
                              ) : (
                                <Icon name="User" size={12} className="text-muted-foreground m-auto mt-0.5" />
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground truncate">{auction?.seller?.full_name || 'Anonymous'}</span>
                            {auction?.seller?.membership_tier && (
                              <span className="ml-auto text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded capitalize flex-shrink-0">
                                {auction?.seller?.membership_tier}
                              </span>
                            )}
                          </div>
                        )}

                        <Button
                          size="sm"
                          fullWidth
                          iconName={isEnded ? 'Eye' : 'Gavel'}
                          iconPosition="left"
                          variant={isEnded ? 'outline' : 'default'}
                          disabled={isEnded}
                        >
                          {isEnded ? 'View Results' : 'Place Bid'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Auction Detail Modal */}
      {selectedAuction && (
        <AuctionFloorDetail
          auction={selectedAuction}
          onClose={() => setSelectedAuction(null)}
          onBidPlaced={() => {
            loadAuctions();
          }}
        />
      )}
    </div>
  );
};

export default AuctionFloor;
